/** Field help/metadata registry for the tour editor.
 *
 * Single source of truth for per-field labels, help text, examples, and
 * import-owned flags. Keys are dot-paths into the PATCH payload — and the draft
 * paths of the record editor's `tour:<id>` record (`record/catalog.ts` builds the
 * catalog from it; `importOwned` / `importForced` drive the fields' import
 * verdicts).
 */
export interface FieldSpec {
  label: string;
  help: string;
  helpLong?: string;
  example?: string;
  /** ⟳ badge: import may overwrite this field on the next run. */
  importOwned?: boolean;
  /** Stronger warning: reset on EVERY import unless the XML sets it. */
  importForced?: boolean;
}

export const FIELD_SPECS: Record<string, FieldSpec> = {
  name: {
    label: 'Name',
    help: 'Trip name shown to guests and staff.',
    example: 'Iceland South Coast — 5 days',
    importOwned: true,
  },
  description: {
    label: 'Description',
    help: 'Short description shown in previews (max 80 characters).',
    importOwned: true,
  },
  start_timezone: {
    label: 'Timezone',
    help: 'IANA timezone in which the trip starts.',
    helpLong: 'Enter a full IANA zone name. A bad value is logged and ignored by the backend.',
    example: 'Europe/Reykjavik',
    importOwned: true,
  },
  use_as_template: {
    label: 'Use as template',
    help: 'Makes this tour selectable as a template for new tours.',
    importForced: true,
  },
  // -- content ------------------------------------------------------------
  'context.tour_description': { label: 'Additional information', help: 'Free-text info shown to guests.', importOwned: true },
  'context.destination': { label: 'Destination', help: 'Destination summary.', importOwned: true },
  'context.highlights': { label: 'Highlights', help: 'Key highlights of the trip.', importOwned: true },
  'context.inclusions': { label: 'Inclusions', help: "What's included.", importOwned: true },
  'context.exclusions': { label: 'Exclusions', help: "What's not included.", importOwned: true },
  'context.packing_items': { label: 'Packing items', help: 'Suggested items to bring.', importOwned: true },
  'context.notes': { label: 'Special instructions', help: 'Special instructions for guests.', importOwned: true },
  'context.notes_link': { label: 'Special instructions link', help: 'URL with more instructions.', importOwned: true },
  'context.local_recommendations': { label: 'Local recommendations', help: 'Local tips.', importOwned: true },
  'context.local_recommendations_link': { label: 'Local recommendations link', help: 'URL with local tips.', importOwned: true },
  'context.cancellation_policy': { label: 'Cancellation policy', help: 'Cancellation policy text.', importOwned: true },
  'context.cancellation_policy_link': { label: 'Cancellation policy link', help: 'URL to the policy.', importOwned: true },
  'context.booking_terms': { label: 'Booking terms', help: 'Terms of booking.', importOwned: true },
  'context.redemption_instructions': { label: 'Redemption instructions', help: 'How to redeem.', importOwned: true },
  'context.usage_instructions': { label: 'Usage instructions', help: 'How to use.', importOwned: true },
  'context.upsell_text': { label: 'Upsell text', help: 'Upsell copy.', importOwned: true },
  'context.upsell_link': { label: 'Upsell link', help: 'Upsell URL.', importOwned: true },
  'context.operated_by': { label: 'Operated by', help: 'Operator name shown to guests.', importOwned: true },
  'context.operated_by_description': { label: 'Operated by description', help: 'Operator description.', importOwned: true },
  'context.tour_type': { label: 'Tour type', help: 'Free-text tour category.', importOwned: true },
  'review.name': { label: 'Review source', help: 'Name of the review platform.', importOwned: true },
  'review.url': { label: 'Review URL', help: 'Link where guests leave reviews.', importOwned: true },
  'photo_sharing.name': { label: 'Photo sharing source', help: 'Name of the photo-sharing service.', importOwned: true },
  'photo_sharing.url': { label: 'Photo sharing URL', help: 'Link to shared photos.', importOwned: true },
  positive_review_target: {
    label: 'Positive feedback target',
    help: 'Where happy guests are directed.',
    importOwned: true,
  },
  positive_review_url: {
    label: 'Positive review URL',
    help: 'Required when the target is "URL".',
    example: 'https://g.page/r/…/review',
    importOwned: true,
  },
  // -- meeting point ------------------------------------------------------
  'meeting_point.name': { label: 'Meeting point name', help: 'Required if a Google Maps URL is set.', importOwned: true },
  'meeting_point.address': { label: 'Address', help: 'Street address of the meeting point.', importOwned: true },
  'meeting_point.latitude': { label: 'Latitude', help: 'Between −90 and 90.', importOwned: true },
  'meeting_point.longitude': { label: 'Longitude', help: 'Between −180 and 180.', importOwned: true },
  'meeting_point.comment': { label: 'Comment', help: 'Extra note about the meeting point.', importOwned: true },
  'meeting_point.maps_url': { label: 'Google Maps URL', help: 'Link to the meeting point on Google Maps.', importOwned: true },
  // -- settings -----------------------------------------------------------
  mytrips_display: { label: 'Show bookings in My Trips', help: 'Display bookings on the guest My Trips screen.' },
  location_sharing: { label: 'Location sharing', help: 'Whether guests share their location.', importOwned: true },
  has_group_chat: {
    label: 'Enable group chat',
    help: 'Creates a group chat channel per departure.',
    helpLong: 'For import-connected tours this flag is reset on every import unless the XML sets group_chat="true".',
    importForced: true,
  },
  exclude_from_chat: { label: 'Disable all chat channels', help: 'Turns off all chat for this tour.' },
  use_operator_defaults: { label: 'Use operator default settings', help: 'Inherit shared settings + tag categories from the operator.' },
  use_operator_pre_check_settings: { label: 'Use operator pre-check settings', help: 'Inherit pre-check-in config from the operator.' },
  allow_selfreg: { label: 'Self-registration page', help: 'Enables a self-registration link for this tour.', importForced: true },
  chat_priority: { label: 'Chat priority', help: 'Order of this tour in the operator’s chat list (set by the system).' },
  internal_name: {
    label: 'Internal name',
    help: 'Only the operator’s team sees it — guests never do. Searchable in the dashboard’s Chat and Trips lists.',
  },
  disable_internal_chat: {
    label: 'Disable internal chat',
    help: 'Turns the guides & staff chat off for this tour only. One-way in effect: it never turns that chat on for an operator that disabled it for every tour. Saving it on retires the conversation — its history is kept.',
  },
  show_all_users_to_guides_in_nonbespoke_tours: {
    label: 'Guides see every guest',
    help: 'Recurring tours: a guide sees all guests of the tour, not only their own. Empty = the operator default; once set here it no longer follows it.',
  },
  effective_display_currency: {
    label: 'Display currency',
    help: 'How prices show in the guest app: the tour’s choice, else its itinerary’s, else the operator’s (set in the dashboard).',
  },
  selfreg_verify_email: {
    label: 'Verify email',
    help: 'Self-registration asks guests to verify their email. Empty = the operator default.',
    helpLong: 'Imports reset this to off on every run unless the XML says verify_email="true".',
    importForced: true,
  },
  selfreg_verify_phone: {
    label: 'Verify phone',
    help: 'Self-registration asks guests to verify their phone. Empty = the operator default.',
    helpLong: 'Imports reset this to off on every run unless the XML says verify_phone="true".',
    importForced: true,
  },
  // -- schedule -----------------------------------------------------------
  start_date: { label: 'Start date', help: 'The day the tour starts. Saving moves its bookings to the new date.', importOwned: true },
  end_date: { label: 'End date', help: 'Derived: the start date plus the itinerary’s days, recomputed on save.' },
  departures: { label: 'Departures', help: 'Departure times — tags of the operator’s “departure” category.' },
  length: {
    label: 'Length',
    help: 'How long the trip runs. Bus trips and exhibitions can set it by hand; otherwise it is derived (the itinerary, the line set’s longest loop).',
  },
  length_is_manual: { label: 'Length set by hand', help: 'Off = derived again on save.' },
  // -- guest journey: waiver texts (notifications) --------------------------
  'context.waiver_required': {
    label: 'Waiver required',
    help: 'Read by notifications (tour.waiver_required). Off removes the key — there is no stored “no”.',
    importOwned: true,
  },
  'context.waiver': { label: 'Waiver text', help: 'Read by notifications (tour.waiver).', importOwned: true },
  'context.waiver_link': { label: 'Waiver link', help: 'Read by notifications (tour.waiver_link).', importOwned: true },
  // -- launch & mapping ---------------------------------------------------
  launch_policy: {
    label: 'Launch policy',
    help: 'Which channels and checks this tour launches with. Empty = the operator default.',
  },
  atag: { label: 'Access tags', help: 'Who may see and manage this tour (access control).' },
  tags: { label: 'System tags', help: 'Tags applied to the tour itself (filters, rules, notifications).' },
  // -- media / relations --------------------------------------------------
  cover_image: { label: 'Cover image', help: 'Shown on the tour card in the guest app.', importOwned: true },
  'meeting_point.photo': { label: 'Meeting point photo', help: 'Photo of the meeting point shown to guests.', importOwned: true },
  crosssells: { label: 'Cross-sold packages', help: 'Packages offered alongside this tour.' },
  vehicle_route_set: { label: 'Line set', help: 'The vehicle route set that supplies this bus trip’s guest content.' },
  // -- identifiers --------------------------------------------------------
  custom_id: {
    label: 'Primary external ID',
    help: 'Matches this tour to imported bookings and OTA products.',
    helpLong: 'Must be unique per operator (including auxiliary IDs).',
    example: 'GYG-345872',
  },
};

export function spec(key: string): FieldSpec {
  return FIELD_SPECS[key] ?? { label: key, help: '' };
}
